import { setFulfilled, setPending, setRejected } from '@ms/yammer-data-support';
import { Reducer } from 'redux';

import { FollowTopicAction, UnfollowTopicAction } from '../../domains/topicFollow/actions.types';
import { RemoveCuratedTopicFulfilledAction } from '../../domains/topics/removeCuratedActions.types';
import { RemoveTopicFulfilledProps } from '../../domains/topics/removeTopicActions';
import { RemoveTopicFulfilledAction } from '../../domains/topics/removeTopicActions.types';
import { TopicsFollowedLoadAction, TopicsFollowedLoadMoreAction } from '../../domains/topicsFollowedLoad/actions.types';
import { Topic } from '../types';

import { TopicsFollowed, TopicsFollowedEdge } from './types';

const loadMoreTopicsFollowed = (state: TopicsFollowed, topicsFollowedEdge: TopicsFollowedEdge) => {
  const currentTopicIds = state.fulfilled?.topicIds ?? [];
  const topicIds = [...currentTopicIds, ...topicsFollowedEdge.topicIds];

  return { ...state, fulfilled: { ...topicsFollowedEdge, topicIds } };
};

const followTopic = (state: TopicsFollowed, topicId: Topic['id']) => {
  const currentFollowedTopics = state.fulfilled?.topicIds ?? [];
  const cursor = state.fulfilled?.cursor ?? null;
  const hasNextPage = state.fulfilled?.hasNextPage ?? false;

  const topicIds = [topicId, ...currentFollowedTopics];

  return { ...state, fulfilled: { cursor, hasNextPage, topicIds } };
};

const removeFromTopicsFollowed = (state: TopicsFollowed, topicId: Topic['id']) => {
  const currentFollowedTopics = state.fulfilled?.topicIds ?? [];
  const cursor = state.fulfilled?.cursor ?? null;
  const hasNextPage = state.fulfilled?.hasNextPage ?? false;

  if (currentFollowedTopics.length === 0) {
    return state;
  }

  return {
    ...state,
    fulfilled: {
      cursor,
      hasNextPage,
      topicIds: currentFollowedTopics.filter((id: Topic['id']) => id !== topicId),
    },
  };
};

const removeTopic = (state: TopicsFollowed, topicIds: RemoveTopicFulfilledProps) => {
  const curatedTopicId = topicIds?.curatedTopicId;

  return curatedTopicId ? removeFromTopicsFollowed(state, curatedTopicId) : state;
};

const initialState: TopicsFollowed = {
  pending: false,
  fulfilled: undefined,
  rejected: undefined,
};

export type TopicsFollowedAction =
  | RemoveCuratedTopicFulfilledAction
  | TopicsFollowedLoadAction
  | TopicsFollowedLoadMoreAction
  | FollowTopicAction
  | UnfollowTopicAction
  | RemoveTopicFulfilledAction;

const reducer: Reducer<TopicsFollowed, TopicsFollowedAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'TOPICS_FOLLOWED.LOAD_PENDING':
      return setPending();
    case 'TOPICS_FOLLOWED.LOAD_REJECTED':
      return setRejected(action.payload.error);
    case 'TOPICS_FOLLOWED.LOAD_FULFILLED':
      return setFulfilled(action.payload.entities.topicsFollowedEdge);
    case 'TOPICS_FOLLOWED.LOAD_MORE_FULFILLED':
      return loadMoreTopicsFollowed(state, action.payload.entities.topicsFollowedEdge);
    case 'TOPIC.FOLLOW_FULFILLED':
      return followTopic(state, action.payload.topicId);
    case 'TOPIC.UNFOLLOW_FULFILLED':
      return removeFromTopicsFollowed(state, action.payload.topicId);
    case 'CURATED_TOPIC.REMOVE_FULFILLED':
      return removeFromTopicsFollowed(state, action.payload.topicId);
    case 'TOPIC.REMOVE_FULFILLED':
      return removeTopic(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
